// AI Steps 4 & 6 shared risk rendering helpers
import React from 'react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Label } from '../../../../components/ui/label';
import { Slider } from '../../../../components/ui/slider';
import { Brain, Shield } from 'lucide-react';
import { cn } from '../../../../lib/utils';

const DEFAULT_VARIABLES = [
  { id: 'validity_reliability', name: 'Validity & Reliability', ticks: ['Untested', 'Minimal', 'Standard', 'Rigorous', 'Continuously Validated'], inverse: true },
  { id: 'safety_security', name: 'Safety & Security', ticks: ['No Controls', 'Basic', 'Standard', 'Hardened', 'Defence-in-Depth'], inverse: true },
  { id: 'transparency_accountability', name: 'Transparency & Accountability', ticks: ['Opaque', 'Minimal', 'Documented', 'Transparent', 'Fully Accountable'], inverse: true },
  { id: 'explainability', name: 'Explainability', ticks: ['Black Box', 'Limited', 'Partial', 'Interpretable', 'Fully Explainable'], inverse: true },
  { id: 'fairness_bias', name: 'Fairness & Bias', ticks: ['No Assessment', 'Aware', 'Measured', 'Mitigated', 'Monitored'], inverse: true },
  { id: 'data_protection_impact', name: 'Impact on Data Subjects', ticks: ['Negligible', 'Minor', 'Moderate', 'Significant', 'Severe'], inverse: false },
  { id: 'personal_data_scope', name: 'Personal Data Scope', ticks: ['None', 'Minimal', 'Moderate', 'Extensive', 'Mass-Scale'], inverse: false },
  { id: 'automated_decisioning', name: 'Automated Decisioning', ticks: ['Advisory', 'Assists', 'Influences', 'Significant', 'Sole'], inverse: false },
];

export function RiskSliders({ riskType, formData, updateField, riskConfig }) {
  const inputs = riskType === 'initial' ? formData.initial_risk_inputs : formData.residual_risk_inputs;
  const variables = riskConfig?.variables || DEFAULT_VARIABLES;

  const updateSlider = (index, value) => {
    const field = riskType === 'initial' ? 'initial_risk_inputs' : 'residual_risk_inputs';
    const newInputs = [...inputs];
    newInputs[index] = value[0];
    updateField(field, newInputs);
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium text-sm text-blue-600 dark:text-blue-400 mb-4 flex items-center gap-2">
          <Brain className="h-4 w-4" />AI Trustworthiness (NIST AI RMF)
        </h4>
        <div className="space-y-5">
          {variables.slice(0, 5).map((variable, index) => (
            <div key={variable.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">{variable.name}</Label>
                <div className="flex items-center gap-2">
                  {variable.inverse && <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950">Higher = Lower Risk</Badge>}
                  <span className="text-sm font-mono w-8 text-right">{inputs[index]}</span>
                </div>
              </div>
              <Slider value={[inputs[index]]} onValueChange={(value) => updateSlider(index, value)} max={100} step={1} className="mt-1" data-testid={`${riskType}-slider-${index}`} />
              <div className="flex justify-between text-xs text-muted-foreground">
                {variable.ticks.map((tick, i) => <span key={i} className="text-center" style={{ width: '20%' }}>{tick}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="pt-4 border-t">
        <h4 className="font-medium text-sm text-purple-600 dark:text-purple-400 mb-4 flex items-center gap-2">
          <Shield className="h-4 w-4" />Data Protection Impact
        </h4>
        <div className="space-y-5">
          {variables.slice(5, 8).map((variable, index) => {
            const actualIndex = 5 + index;
            return (
              <div key={variable.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{variable.name}</Label>
                  <div className="flex items-center gap-2">
                    {!variable.inverse && <Badge variant="outline" className="text-xs bg-purple-50 dark:bg-purple-950">Higher = Higher Risk</Badge>}
                    <span className="text-sm font-mono w-8 text-right">{inputs[actualIndex]}</span>
                  </div>
                </div>
                <Slider value={[inputs[actualIndex]]} onValueChange={(value) => updateSlider(actualIndex, value)} max={100} step={1} className="mt-1" data-testid={`${riskType}-slider-${actualIndex}`} />
                <div className="flex justify-between text-xs text-muted-foreground">
                  {variable.ticks.map((tick, i) => <span key={i} className="text-center" style={{ width: '20%' }}>{tick}</span>)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function DualDimensionResult({ riskType, formData }) {
  const score = riskType === 'initial' ? formData.initial_risk_score : formData.residual_risk_score;
  const level = riskType === 'initial' ? formData.initial_risk_level : formData.residual_risk_level;
  const label = riskType === 'initial' ? formData.initial_risk_label : formData.residual_risk_label;
  const action = riskType === 'initial' ? formData.initial_risk_action : formData.residual_risk_action;
  const trustScore = riskType === 'initial' ? formData.initial_trustworthiness_score : formData.residual_trustworthiness_score;
  const dpScore = riskType === 'initial' ? formData.initial_data_protection_score : formData.residual_data_protection_score;

  if (score === null) return null;

  const colorMap = {
    Low: { ring: '#059669', bg: 'bg-green-500' },
    Medium: { ring: '#D97706', bg: 'bg-amber-500' },
    High: { ring: '#DC2626', bg: 'bg-orange-500' },
    Critical: { ring: '#991B1B', bg: 'bg-red-600' },
  };
  const colors = colorMap[level] || colorMap.Medium;

  const ScoreRing = ({ size, stroke, color, value, children }) => (
    <div className={`relative ${size} mx-auto`}>
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth={stroke} className="text-muted/20" />
        <circle cx="50" cy="50" r="45" fill="none" strokeWidth={stroke} strokeLinecap="round" stroke={color} strokeDasharray={`${value * 2.83} 283`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );

  return (
    <Card className="mt-4">
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <ScoreRing size="w-20 h-20" stroke="8" color="#3B82F6" value={trustScore}>
              <span className="text-lg font-bold text-blue-600">{Math.round(trustScore)}</span>
            </ScoreRing>
            <p className="text-xs text-muted-foreground mt-2">AI Trustworthiness</p>
            <p className="text-xs text-blue-600">(Lower = Better)</p>
          </div>
          <div className="text-center">
            <ScoreRing size="w-24 h-24" stroke="10" color={colors.ring} value={score}>
              <span className="text-2xl font-bold">{Math.round(score)}</span>
            </ScoreRing>
            <Badge className={cn("mt-2", colors.bg)}>{level} Risk</Badge>
          </div>
          <div className="text-center">
            <ScoreRing size="w-20 h-20" stroke="8" color="#9333EA" value={dpScore}>
              <span className="text-lg font-bold text-purple-600">{Math.round(dpScore)}</span>
            </ScoreRing>
            <p className="text-xs text-muted-foreground mt-2">Data Protection Impact</p>
            <p className="text-xs text-purple-600">(Lower = Better)</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t text-center">
          <p className="font-medium">{label}</p>
          <p className="text-sm text-muted-foreground mt-1">{action}</p>
        </div>
      </CardContent>
    </Card>
  );
}
